import { useState } from "react";
import { FaEye, FaEyeSlash, FaInfoCircle, FaLock } from "react-icons/fa";
import HintTrigger from "../HintTrigger/HintTrigger";

type InputProps = {
  label?: string;
  name?: string;
  value?: string | number | undefined | boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  type?: "text" | "email" | "password" | "number" | "date" | "textarea" | "tel" | "checkbox";
  leftIcon?: React.ReactNode;
  className?: string;
  rows?: number;
  maxLength?: number;
  readOnly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  tooltip?: string;
  rightIcon?: React.ReactNode;
};

export const Input = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  type = "text",
  leftIcon,
  className,
  rows,
  maxLength,
  readOnly = false,
  min,
  max,
  step,
  disabled,
  tooltip,
  rightIcon
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const inputBaseStyles = "w-full pr-4 py-2.5 sm:py-3 leading-6 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600";
  const paddingLeft = leftIcon ? "pl-10" : "pl-4";
  const paddingRight = isPassword ? "pr-10" : "pr-4";
  const borderColor = error ? "border-red-500" : "border-gray-300";

  const normalizeNumberInput = (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return "";
    }

    if (trimmed.includes(".")) {
      const [intPart, fracPart] = trimmed.split(".");
      const normalizedInt = intPart.replace(/^0+(?=\d)/, "");
      const safeInt = normalizedInt === "" ? "0" : normalizedInt;
      return fracPart !== undefined ? `${safeInt}.${fracPart}` : safeInt;
    }

    return trimmed.replace(/^0+(?=\d)/, "");
  };

  const handleNumberBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (type !== "number") {
      return;
    }

    const normalized = normalizeNumberInput(event.currentTarget.value);
    if (normalized === event.currentTarget.value || !onChange) {
      return;
    }

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name ?? event.target.name,
        value: normalized
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (type !== "number" || !onChange) {
      onChange?.(event);
      return;
    }

    const normalized = normalizeNumberInput(event.target.value);
    if (normalized === event.target.value) {
      onChange(event);
      return;
    }

    onChange({
      ...event,
      target: {
        ...event.target,
        name: name ?? event.target.name,
        value: normalized
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  if (type === "checkbox") {
    const isCheckboxReadOnly = Boolean(readOnly);
    const isCheckboxDisabled = Boolean(disabled);
    const showCheckboxHover = !isCheckboxReadOnly;
    const checkboxWrapperClass = isCheckboxReadOnly ? "cursor-not-allowed" : "";
    const checkboxInputClass = isCheckboxReadOnly ? "pointer-events-none" : "";

    const handleCheckboxClick = (event: React.MouseEvent<HTMLInputElement>) => {
      if (isCheckboxReadOnly) {
        event.preventDefault();
      }
    };

    return (
      <div className="flex items-center gap-2 mb-4">
        <span className={checkboxWrapperClass}>
          <input
            type="checkbox"
            name={name}
            checked={Boolean(value)}
            onChange={onChange}
            readOnly={isCheckboxReadOnly}
            disabled={isCheckboxDisabled}
            onClick={handleCheckboxClick}
            className={`w-4 h-4 border-gray-300 rounded accent-primary-600 ${checkboxInputClass} ${
              isCheckboxReadOnly || isCheckboxDisabled ? "cursor-not-allowed" : ""
            } ${showCheckboxHover ? "hover:accent-primary-700 focus:ring-primary-700" : ""}`}
          />
        </span>
        {label && (
          <label htmlFor={name} className="text-gray-600 text-sm">
            {label} {required && <span className="text-primary-600">*</span>}
          </label>
        )}
      </div>
    );
  }

  return (
    <div className="w-full sm:w-auto">
      {label && (
        <label className="block mb-1 text-gray-600 text-sm font-medium">
          {label}{required && <span className="text-primary-600">*</span>}
          {tooltip && (
            <HintTrigger label="Requisitos de senha" hint={tooltip} className="ml-1 inline-flex align-middle text-gray-400 cursor-help">
              <FaInfoCircle />
            </HintTrigger>
          )}
        </label>
      )}
      <div className="relative w-full sm:w-auto">
        {type === "textarea" ? (
          <textarea
            name={name}
            value={value?.toString()}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={`${inputBaseStyles} ${paddingLeft} ${paddingRight} ${borderColor} max-h-96 ${className ?? ""}`}
            rows={rows}
          />
        ) : (
          <input
            type={isPassword ? (showPassword ? "text" : "password") : type}
            name={name}
            value={typeof value === "string" ? String(value) : Number(value)}
            onChange={handleInputChange}
            placeholder={placeholder}
            maxLength={maxLength}
            className={`${inputBaseStyles} ${paddingLeft} ${paddingRight} ${borderColor} ${
              readOnly || disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
            } ${className ?? ""}`}
            readOnly={readOnly}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            onWheel={type === "number" ? (e) => (e.currentTarget as HTMLInputElement).blur() : undefined}
            onKeyDown={type === "number" ? (e) => {
              if (["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
              }
            } : undefined}
            onBlur={type === "number" ? handleNumberBlur : undefined}
          />
        )}
        {!readOnly && leftIcon && (
          <span className="absolute left-3 top-3.5 text-gray-400 pointer-events-none">
            {leftIcon}
          </span>
        )}
        {rightIcon && (
          <span className="absolute right-3 top-3.5 text-gray-400 pointer-events-none">
            {rightIcon}
          </span>
        )}
        {readOnly && !leftIcon && !rightIcon && (
          <span className="absolute right-3 top-3.5 text-gray-400 pointer-events-none">
            <FaLock className="text-[13px] sm:text-base" />
          </span>
        )}
        {isPassword && !readOnly && !rightIcon && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(event) => {
              event.stopPropagation();
              setShowPassword((prev) => !prev);
            }}
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        )}
      </div>
      {error && <p className="text-sm text-primary-600 mt-1">{error}</p>}
    </div>
  );
};
